<template>
<div>
    <p class="text-muted small">
        Ministry of Human Services &amp; Social Security — Public Assistance
        (Medical / Economical) application + Medical Inquiry / Review.
    </p>

    <b-form @submit.prevent="onSubmit" novalidate>
        <h6>Applicant data</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="First name"><b-form-input v-model="form.first_name" /></b-form-group>
                <b-form-group label="Middle name"><b-form-input v-model="form.middle_name" /></b-form-group>
                <b-form-group label="Surname"><b-form-input v-model="form.surname" /></b-form-group>
                <b-form-group label="Other name"><b-form-input v-model="form.other_name" /></b-form-group>
                <b-form-group label="Current address"><b-form-textarea rows="2" v-model="form.address" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Sex">
                    <b-form-radio-group v-model="form.sex" :options="[{value:'male',text:'Male'},{value:'female',text:'Female'}]" />
                </b-form-group>
                <b-form-group label="Date of birth"><b-form-datepicker v-model="form.dob" /></b-form-group>
                <b-form-group label="ID card #"><b-form-input v-model="form.id_card" /></b-form-group>
                <b-form-group label="Passport #"><b-form-input v-model="form.passport" /></b-form-group>
                <b-form-group label="Region"><b-form-input v-model="form.region" /></b-form-group>
                <b-form-group label="District"><b-form-input v-model="form.district" /></b-form-group>
                <b-row>
                    <b-col><b-form-group label="Landline"><b-form-input v-model="form.phone_landline" /></b-form-group></b-col>
                    <b-col><b-form-group label="Cell"><b-form-input v-model="form.phone_cell" /></b-form-group></b-col>
                </b-row>
            </b-col>
        </b-row>

        <h6>Claimed service</h6>
        <b-form-group>
            <b-form-radio-group v-model="form.claimed_service" :options="[
                { value: 'medical', text: 'Public Assistance — Medical' },
                { value: 'economical', text: 'Public Assistance — Economical' }
            ]" />
        </b-form-group>
        <b-form-group label="Application date"><b-form-datepicker v-model="form.application_date" /></b-form-group>

        <h6>Medical inquiry</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Medical practitioner name"><b-form-input v-model="form.practitioner_name" /></b-form-group>
                <b-form-group label="Medical condition type"><b-form-input v-model="form.condition_type" /></b-form-group>
                <b-form-group label="Likely to recover"><b-form-input v-model="form.likely_recover" /></b-form-group>
                <b-form-group label="Impact on capacities"><b-form-textarea rows="2" v-model="form.impact" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Disability level">
                    <b-form-radio-group v-model="form.disability_level" :options="[{value:'temporary',text:'Temporary'},{value:'permanent',text:'Permanent'}]" />
                </b-form-group>
                <b-form-group label="General description of disabilities"><b-form-textarea rows="2" v-model="form.disabilities_desc" /></b-form-group>
                <b-form-group label="Effect on daily living &amp; employment"><b-form-textarea rows="2" v-model="form.daily_effect" /></b-form-group>
                <b-form-group label="Legal representative (if applicable)"><b-form-input v-model="form.legal_rep" /></b-form-group>
            </b-col>
        </b-row>

        <h6>Adult-only additional info</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Union status">
                    <b-form-select v-model="form.union_status" :options="['Single','Married','Divorced','Separated','Common Law','Visiting Relation']" />
                </b-form-group>
                <b-form-group label="Employment status">
                    <b-form-radio-group v-model="form.employment_status" :options="[{value:'employed',text:'Employed'},{value:'unemployed',text:'Unemployed'}]" />
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Receiving other assistance?"><b-form-radio-group v-model="form.other_assistance" :options="[{value:'yes',text:'Yes'},{value:'no',text:'No'}]" /></b-form-group>
                <b-form-group label="Monthly amount (GYD)"><b-form-input type="number" v-model="form.other_amount" /></b-form-group>
            </b-col>
        </b-row>

        <div class="text-right mt-3">
            <b-button variant="secondary" @click="$emit('close')">Cancel</b-button>
            <b-button type="submit" variant="success">
                <i class="fa-solid fa-file-arrow-down"></i> Generate PDF
            </b-button>
        </div>
    </b-form>
</div>
</template>

<script>
import { formatDate, buildAddress, loadCoords } from "./pdfFiller"
import formPersistence from "./formPersistence"

export default {
    name: "public-assistance-form",
    mixins: [formPersistence],
    props: { demographics: { type: Object, default: () => ({}) } },
    data() {
        return {
            form: {
                first_name: "", middle_name: "", surname: "", other_name: "",
                address: "", sex: "", dob: "", id_card: "", passport: "",
                region: "", district: "", phone_landline: "", phone_cell: "",
                claimed_service: "medical",
                application_date: new Date().toISOString().slice(0, 10),
                practitioner_name: "", condition_type: "", likely_recover: "",
                impact: "", disability_level: "temporary", disabilities_desc: "",
                daily_effect: "", legal_rep: "",
                union_status: "Single", employment_status: "unemployed",
                other_assistance: "no", other_amount: "",
            },
        }
    },
    watch: {
        demographics: {
            immediate: true,
            handler(d) {
                if (!d) return
                if (!this.form.first_name) this.form.first_name = d.first_name || ""
                if (!this.form.middle_name) this.form.middle_name = d.middle_name || ""
                if (!this.form.surname) this.form.surname = d.surname || ""
                if (!this.form.address) this.form.address = buildAddress(d)
                if (!this.form.dob) this.form.dob = d.date_of_birth || ""
                if (d.id_number) {
                    const isPassport = (d.id_type?.name || "").toLowerCase().includes("passport")
                    if (isPassport) {
                        if (!this.form.passport) this.form.passport = d.id_number
                    } else if (!this.form.id_card) {
                        this.form.id_card = d.id_number
                    }
                }
                if (!this.form.district && d.district) this.form.district = d.district?.name || d.district
                if (!this.form.phone_landline) this.form.phone_landline = d.phone_landline || ""
                if (!this.form.phone_cell && Array.isArray(d.phone_mobile) && d.phone_mobile.length) {
                    this.form.phone_cell = d.phone_mobile[0]
                }
                if (!this.form.sex && d.biological_gender?.name) {
                    this.form.sex = d.biological_gender.name.toLowerCase().startsWith("f") ? "female" : "male"
                }
                if (d.marital_status?.name) {
                    const unions = ["Single", "Married", "Divorced", "Separated", "Common Law", "Visiting Relation"]
                    if (unions.includes(d.marital_status.name)) this.form.union_status = d.marital_status.name
                }
                if (!this.form.practitioner_name && d.primary_physician?.name) {
                    this.form.practitioner_name = d.primary_physician.name
                }
            },
        },
    },
    methods: {
        buildCoords() {
            // Approximate coords for public-assistance-medical.pdf.
            return {
                first_name: { page: 0, x: 175, y: 680 },
                middle_name: { page: 0, x: 175, y: 655 },
                surname: { page: 0, x: 175, y: 630 },
                other_name: { page: 0, x: 175, y: 605 },
                address: { page: 0, x: 175, y: 570 },
                sex_male: { page: 0, x: 460, y: 680, type: "check" },
                sex_female: { page: 0, x: 460, y: 660, type: "check" },
                dob: { page: 0, x: 440, y: 655 },
                id_card: { page: 0, x: 440, y: 630 },
                passport: { page: 0, x: 440, y: 605 },
                region: { page: 0, x: 440, y: 580 },
                district: { page: 0, x: 440, y: 558 },
                phone_landline: { page: 0, x: 175, y: 525 },
                phone_cell: { page: 0, x: 350, y: 525 },
                claimed_medical: { page: 0, x: 95, y: 470, type: "check" },
                claimed_economical: { page: 0, x: 320, y: 470, type: "check" },
                application_date: { page: 0, x: 360, y: 490 },

                practitioner_name: { page: 1, x: 200, y: 525 },
                condition_type: { page: 1, x: 130, y: 470 },
                likely_recover: { page: 1, x: 300, y: 470 },
                impact: { page: 1, x: 410, y: 470 },
                disability_temporary: { page: 1, x: 220, y: 405, type: "check" },
                disability_permanent: { page: 1, x: 320, y: 405, type: "check" },
                disabilities_desc: { page: 1, x: 230, y: 355 },
                daily_effect: { page: 1, x: 230, y: 305 },
                legal_rep: { page: 1, x: 230, y: 260 },

                union_status: { page: 2, x: 230, y: 700 },
                employment_status_employed: { page: 2, x: 245, y: 638, type: "check" },
                employment_status_unemployed: { page: 2, x: 340, y: 638, type: "check" },
                other_assistance_yes: { page: 2, x: 320, y: 458, type: "check" },
                other_assistance_no: { page: 2, x: 370, y: 458, type: "check" },
                other_amount: { page: 2, x: 280, y: 420 },
            }
        },
        buildValues() {
            const f = this.form
            return {
                first_name: f.first_name,
                middle_name: f.middle_name,
                surname: f.surname,
                other_name: f.other_name,
                address: f.address,
                sex_male: f.sex === "male",
                sex_female: f.sex === "female",
                dob: formatDate(f.dob),
                id_card: f.id_card,
                passport: f.passport,
                region: f.region,
                district: f.district,
                phone_landline: f.phone_landline,
                phone_cell: f.phone_cell,
                claimed_medical: f.claimed_service === "medical",
                claimed_economical: f.claimed_service === "economical",
                application_date: formatDate(f.application_date),

                practitioner_name: f.practitioner_name,
                condition_type: f.condition_type,
                likely_recover: f.likely_recover,
                impact: f.impact,
                disability_temporary: f.disability_level === "temporary",
                disability_permanent: f.disability_level === "permanent",
                disabilities_desc: f.disabilities_desc,
                daily_effect: f.daily_effect,
                legal_rep: f.legal_rep,

                union_status: f.union_status,
                employment_status_employed: f.employment_status === "employed",
                employment_status_unemployed: f.employment_status === "unemployed",
                other_assistance_yes: f.other_assistance === "yes",
                other_assistance_no: f.other_assistance === "no",
                other_amount: f.other_amount,
            }
        },
        async onSubmit() {
            try {
                const coords = (await loadCoords("public-assistance-medical")) || this.buildCoords()
                await this.fillAndPersist({ form_key: "public-assistance", title: "Public Assistance Medical" }, {
                    templateUrl: "/forms/public-assistance-medical.pdf",
                    outputName: `public-assistance-${this.form.surname || "patient"}.pdf`,
                    values: this.buildValues(),
                    coords,
                    xOffset: 8,
                })
                this.$emit("close")
            } catch (e) {
                this.$bvToast.toast(e.message || "Could not generate PDF", {
                    title: "PDF generation failed",
                    variant: "danger",
                })
            }
        },
    },
}
</script>
